import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import {
  Navigate,
  useLocation,
  useParams,
  type RouteObject,
} from "react-router-dom";

/**
 * Slot for the active product workspace breadcrumb — populated from
 * `ProductWorkspacePage` so the trail reads "Onboardings > Returns" instead
 * of the static literal "Workspace".
 */
export const productWorkspaceBreadcrumb = breadcrumbSlot();

function LegacyMerchantDomainViewRedirect() {
  const { publicDomain } = useParams();
  const { search } = useLocation();
  const domain = publicDomain ?? "";
  return (
    <Navigate
      replace
      to={`/onboardings/legacy/merchants/view/domain/${encodeURIComponent(domain)}${search}`}
    />
  );
}

/**
 * Legacy `/onboardings/analytics/:slice[/tab]` URLs still exist in links and
 * bookmarks. Redirect them into the new per-product workspace tree.
 */
function LegacyAnalyticsRedirect() {
  const { search } = useLocation();
  const params = useParams();
  const slice = params.slice ?? "overview";
  const innerTab = params.tab;
  let product: string;
  switch (slice) {
    case "overview": {
      product = "all";
      break;
    }
    case "returns": {
      product = "returns";
      break;
    }
    case "order-editing":
    case "oms": {
      product = "order-editing";
      break;
    }
    default:
      product = "all";
  }
  // Map the old inner tab name ("overview"/"funnel"/"audit") onto the new
  // workspace tab name.
  const tab =
    innerTab == null || innerTab === "overview" ? "analytics" : innerTab;
  return <Navigate replace to={`/onboardings/${product}/${tab}${search}`} />;
}

/**
 * Legacy `/onboardings/merchants[/:tab]` list URLs redirect into
 * `/onboardings/:product/merchants`.
 */
function LegacyMerchantsListRedirect() {
  const { tab } = useParams();
  const { search } = useLocation();
  const product = tab == null || tab === "" || tab === "all" ? "all" : tab;
  return <Navigate replace to={`/onboardings/${product}/merchants${search}`} />;
}

function OnboardingV2UrlRedirect() {
  const location = useLocation();
  const search = location.search;
  return <Navigate replace to={`/onboardings/all/merchants${search}`} />;
}

export const onboardingsRoutes: RouteObject[] = [
  {
    handle: { breadcrumb: "Onboardings" },
    async lazy() {
      const { Page } = await import("../app/page");
      return { element: <Page hidePadding={() => true} /> };
    },
    children: [
      { index: true, element: <Navigate replace to="all/merchants" /> },

      // Back-compat redirects for bookmarks + older links.
      { path: "v2", element: <OnboardingV2UrlRedirect /> },
      { path: "v2/*", element: <OnboardingV2UrlRedirect /> },
      {
        path: "merchants",
        children: [
          {
            index: true,
            element: <Navigate replace to="/onboardings/all/merchants" />,
          },
          {
            path: "view/domain/:publicDomain",
            element: <LegacyMerchantDomainViewRedirect />,
          },
          {
            path: "view/:teamId",
            handle: { breadcrumb: "Details" },
            async lazy() {
              const { V2MerchantDetailPage } = await import(
                "./v2/V2MerchantDetailPage"
              );
              return { Component: V2MerchantDetailPage };
            },
          },
          { path: ":tab", element: <LegacyMerchantsListRedirect /> },
        ],
      },
      {
        path: "analytics",
        children: [
          {
            index: true,
            element: <Navigate replace to="/onboardings/all/analytics" />,
          },
          { path: ":slice", element: <LegacyAnalyticsRedirect /> },
          { path: ":slice/:tab", element: <LegacyAnalyticsRedirect /> },
        ],
      },

      // Cross-product onboarding ops surface. Lives outside the per-product
      // workspace because the controls inside (HubSpot sync health, feature
      // flags, env inspection) apply to all onboarding products at once.
      {
        path: "settings",
        handle: { breadcrumb: "Settings" },
        children: [
          { index: true, element: <Navigate replace to="sync" /> },
          {
            path: ":section",
            async lazy() {
              const { SettingsPage } = await import("./settings/SettingsPage");
              return { Component: SettingsPage };
            },
          },
        ],
      },

      // Legacy (pre-v2) tree is untouched.
      {
        path: "legacy",
        handle: { breadcrumb: "Legacy" },
        children: [
          { index: true, element: <Navigate replace to="merchants" /> },
          {
            path: "merchants",
            handle: { breadcrumb: "Merchants" },
            children: [
              { index: true, element: <Navigate replace to="all" /> },
              {
                path: "view/:merchantId",
                handle: { breadcrumb: "Details" },
                async lazy() {
                  const { MerchantDetailPage } = await import(
                    "./merchants/MerchantDetailPage"
                  );
                  return { Component: MerchantDetailPage };
                },
              },
              {
                path: "view/domain/:publicDomain",
                handle: { breadcrumb: "Configuration Details" },
                async lazy() {
                  const { MerchantDetailPage } = await import(
                    "./merchants/MerchantDetailPage"
                  );
                  return { Component: MerchantDetailPage };
                },
              },
              {
                path: ":tab",
                async lazy() {
                  const { MerchantsPage } = await import(
                    "./merchants/MerchantsPage"
                  );
                  return { Component: MerchantsPage };
                },
              },
            ],
          },
        ],
      },

      // New per-product workspace tree.
      {
        path: ":product",
        handle: { breadcrumb: productWorkspaceBreadcrumb },
        children: [
          { index: true, element: <Navigate replace to="merchants" /> },
          {
            path: ":tab",
            async lazy() {
              const { ProductWorkspacePage } = await import(
                "./workspace/ProductWorkspacePage"
              );
              return { Component: ProductWorkspacePage };
            },
          },
        ],
      },
    ],
  },
];
